import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Sparkles, Send, Pause, Save, ArrowRight, CheckCircle, Clock, AlertCircle, Lightbulb } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import { trackEvent } from '@/lib/monitoring'
import type { IntakeMessage, IntakeThreadResponse, RoleHighlights } from '@/lib/contracts'

const FALLBACK_PROJECT_ID = 'alpha-grid'

// ─── Intake framework — 6 areas we need to cover for a complete brief
// Each area is "done" when its signal keywords appear in highlights or messages.

interface ProgressStep {
  label: string
  done: boolean
  active: boolean
}

function computeProgressSteps(
  highlights: { mustHaves: string[]; niceToHaves: string[]; leadershipSignals: string[] },
  messages: { sender: string; text: string }[],
  persistedDone: Set<string>,
): ProgressStep[] {
  // Build a corpus from highlights + the last 3 recruiter messages for signal matching
  const recruiterMessages = messages.filter((m) => m.sender === 'recruiter')
  // Use ALL recruiter messages — not just last 3 — so early answers aren't missed
  const allRecruiterText = recruiterMessages.map((m) => m.text).join(' ')
  const allText = [
    ...highlights.mustHaves,
    ...highlights.niceToHaves,
    ...highlights.leadershipSignals,
    allRecruiterText,
  ].join(' ').toLowerCase()

  const recruiterTurns = recruiterMessages.length

  // Helper: check if any signal keyword appears in the corpus
  const hasSignal = (signals: string[]) => signals.some((s) => allText.includes(s))

  const areas: { label: string; done: boolean }[] = [
    {
      label: 'Role Scope',
      // Always done — role context comes from the project title at setup
      done: true,
    },
    {
      label: 'Why This Role',
      done: hasSignal(['new position', 'replacement', 'why now', 'created', 'headcount', 'expansion', 'new hire', 'backfill', 'new seat', 'new role', 'grow', 'growing', 'scale', 'expand', 'above them', 'sit above', 'level', 'manager', 'promote', 'promoted']),
    },
    {
      label: 'Commercial Goals',
      done: hasSignal(['arr', 'revenue', 'quota', 'target', 'commercial', 'million', 'ote', 'acv', 'sales cycle', 'deal size']),
    },
    {
      label: 'Team & Structure',
      done: hasSignal(['team', 'headcount', 'reps', 'aes', 'sdrs', 'inherit', 'org', 'hire', 'structure', 'reports']),
    },
    {
      label: 'Culture & Fit',
      done: hasSignal(['culture', 'values', 'successful', 'thrive', 'competitor', 'target companies', 'dealbreaker', 'deal breaker', 'red flag']),
    },
    {
      label: 'Must-Haves',
      done: highlights.mustHaves.length >= 2,
    },
    {
      label: 'Dealbreakers',
      done: hasSignal(['fail', 'wrong', 'gone wrong', 'mistake', 'avoid', 'risk', 'concern', 'previous hire', 'past hire', 'what went wrong', 'dealbreaker', 'deal breaker', 'not into', 'don\'t want', 'must not', 'cannot', 'can\'t', 'red flag', 'absolute', 'flip side', 'data', 'numbers', 'understand', 'explain']),
    },
    {
      label: 'Year 1 Success',
      done: hasSignal(['success', '90 day', '180 day', '12 month', 'goal', 'milestone', 'kpi', 'year one', 'first year', 'looks like']),
    },
  ]

  // Merge with persisted completed steps — once green, always green
  const steps: ProgressStep[] = areas.map((area) => ({
    label: area.label,
    done: area.done || persistedDone.has(area.label),
    active: false,
  }))

  // Mark the first incomplete step as active (if there are enough recruiter turns)
  if (recruiterTurns > 0) {
    const firstIncomplete = steps.findIndex((s) => !s.done)
    if (firstIncomplete >= 0) steps[firstIncomplete].active = true
  }

  return steps
}

export const ConversationalIntake = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const navigate = useNavigate()

  const [messages, setMessages] = useState<IntakeMessage[]>([])
  const [highlights, setHighlights] = useState<RoleHighlights>({
    mustHaves: [],
    niceToHaves: [],
    leadershipSignals: [],
  })
  const [inputText, setInputText] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  // Track completed steps persistently so they never revert to orange
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())
  const scrollRef = useRef<HTMLDivElement>(null)

  const applyThread = (thread: IntakeThreadResponse) => {
    setMessages(thread.messages)
    setHighlights(thread.highlights)
  }

  // Accumulate completed steps so they stay green permanently
  useEffect(() => {
    const steps = computeProgressSteps(highlights, messages, completedSteps)
    const newlyDone = steps.filter((s) => s.done).map((s) => s.label)
    if (newlyDone.some((label) => !completedSteps.has(label))) {
      setCompletedSteps((prev) => new Set([...prev, ...newlyDone]))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlights, messages])

  // Elapsed timer
  useEffect(() => {
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000)
    return () => clearInterval(interval)
  }, [])

  const formatElapsed = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    engineApiClient
      .getIntakeThread(projectId)
      .then(applyThread)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load intake'))
      .finally(() => setIsLoading(false))
  }, [projectId])

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = useCallback(async () => {
    const trimmed = inputText.trim()
    if (!trimmed || isSending) return
    setIsSending(true)
    setError(null)
    setInputText('')
    try {
      const thread = await engineApiClient.postIntakeMessage(projectId, { text: trimmed })
      trackEvent('intake_message_sent', { projectId })
      applyThread(thread)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setIsSending(false)
    }
  }, [inputText, isSending, projectId])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="bg-[#02182B] min-h-screen flex flex-col">

      {/* Header bar */}
      <div className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white text-sm font-medium">Session Active</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 text-sm">
              <Clock size={13} />
              <span>{formatElapsed(elapsedSeconds)}</span>
            </div>
            <span className="px-2 py-0.5 bg-[#FD802E]/10 text-[#FD802E] rounded-full text-xs font-medium">
              Intake Workflow
            </span>
          </div>
          <span className="text-gray-400 text-sm">Erica · AI Intake</span>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 gap-6">

        {/* Left — Progress tracker (live, driven by highlights + messages) */}
        <div className="hidden lg:block w-52 flex-shrink-0">
          <p className="text-xs uppercase text-gray-400 font-medium mb-4">Intake Progress</p>
          <nav className="space-y-1">
            {computeProgressSteps(highlights, messages, completedSteps).map((step) => (
              <div key={step.label} className="flex items-center gap-2.5 px-2 py-2">
                {step.done ? (
                  <CheckCircle size={16} className="text-green-400 shrink-0" />
                ) : step.active ? (
                  <span className="w-4 h-4 rounded-full border-2 border-[#FD802E] bg-[#FD802E]/20 shrink-0 animate-pulse" />
                ) : (
                  <span className="w-4 h-4 rounded-full border-2 border-[#2a4a5c] shrink-0" />
                )}
                <span className={`text-sm leading-snug ${
                  step.done ? 'text-green-400' :
                  step.active ? 'text-[#FD802E] font-medium' :
                  'text-gray-600'
                }`}>
                  {step.label}
                </span>
              </div>
            ))}
          </nav>

          {/* Mini coverage summary */}
          {(() => {
            const steps = computeProgressSteps(highlights, messages, completedSteps)
            const doneCount = steps.filter((s) => s.done).length
            const totalSteps = steps.length
            const pct = (doneCount / totalSteps) * 100
            const allDone = doneCount === totalSteps
            return (
              <div className="mt-6 px-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-500">Coverage</span>
                  <span className={`font-medium ${allDone ? 'text-green-400' : 'text-[#FD802E]'}`}>
                    {doneCount}/{totalSteps}
                  </span>
                </div>
                <div className="h-1.5 bg-[#0a2d42] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      allDone ? 'bg-green-400' : 'text-[#FD802E] bg-[#FD802E]'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })()}
        </div>

        {/* Centre — Chat panel */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[calc(100vh-340px)] min-h-[300px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                Loading intake thread…
              </div>
            ) : error ? (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 text-center">
                {error}
                <button
                  onClick={() => {
                    setError(null)
                    setIsLoading(true)
                    engineApiClient.getIntakeThread(projectId).then(applyThread).catch((e) => setError(e.message)).finally(() => setIsLoading(false))
                  }}
                  className="ml-3 underline hover:no-underline"
                >
                  Retry
                </button>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'erica' ? 'justify-start' : 'justify-end'}`}
                >
                  {msg.sender === 'erica' && (
                    <div className="w-7 h-7 bg-[#FD802E]/10 rounded-full flex items-center justify-center mr-2 mt-1 shrink-0">
                      <Sparkles size={13} className="text-[#FD802E]" />
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-xl px-4 py-3 text-sm ${
                    msg.sender === 'erica'
                      ? 'bg-[#233D4C] text-white'
                      : 'bg-[#FD802E] text-[#233D4C] font-medium'
                  }`}>
                    <p className={`text-xs mb-1 ${msg.sender === 'erica' ? 'text-gray-400' : 'text-[#233D4C]/70'}`}>
                      {msg.sender === 'erica' ? 'Erica' : 'You'}
                    </p>
                    <p className="leading-relaxed">{msg.text}</p>
                    <p className={`text-xs mt-1.5 ${msg.sender === 'erica' ? 'text-gray-500' : 'text-[#233D4C]/50'}`}>
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-[#233D4C] rounded-xl px-4 py-3 flex items-end gap-[3px]">
                  <style>{`
                    @keyframes erica-wave-1 { 0%,100%{height:4px} 50%{height:14px} }
                    @keyframes erica-wave-2 { 0%,100%{height:6px} 50%{height:18px} }
                    @keyframes erica-wave-3 { 0%,100%{height:4px} 50%{height:20px} }
                    @keyframes erica-wave-4 { 0%,100%{height:6px} 50%{height:16px} }
                    @keyframes erica-wave-5 { 0%,100%{height:4px} 50%{height:12px} }
                  `}</style>
                  {[
                    { kf: 'erica-wave-1', dur: '0.8s', delay: '0s' },
                    { kf: 'erica-wave-2', dur: '0.6s', delay: '0.1s' },
                    { kf: 'erica-wave-3', dur: '0.7s', delay: '0.05s' },
                    { kf: 'erica-wave-4', dur: '0.9s', delay: '0.15s' },
                    { kf: 'erica-wave-5', dur: '0.75s', delay: '0.08s' },
                  ].map((b, i) => (
                    <span key={i} className="w-[4px] rounded-full bg-[#FD802E]"
                      style={{ animationName: b.kf, animationDuration: b.dur, animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite', animationDelay: b.delay, height: '4px', display: 'block' }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </div>

          {/* Input area */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl p-3 flex gap-3 items-end">
            <textarea
              className="flex-1 resize-none bg-transparent text-white text-sm placeholder:text-gray-500 focus:outline-none leading-relaxed"
              rows={2}
              placeholder="Reply to Erica… (Enter to send)"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading || isSending}
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim() || isSending}
              className="bg-[#FD802E] text-[#233D4C] p-2 rounded-lg hover:bg-[#ff8f45] transition-colors disabled:opacity-50 shrink-0"
            >
              <Send size={16} />
            </button>
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-2">
              <button className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-gray-300 text-sm px-3 py-2 rounded-lg hover:text-white transition-colors">
                <Pause size={13} /> Pause Session
              </button>
              <button className="flex items-center gap-1.5 bg-[#233D4C] border border-[#2a4a5c] text-gray-300 text-sm px-3 py-2 rounded-lg hover:text-white transition-colors">
                <Save size={13} /> Save Draft
              </button>
            </div>
            <button
              onClick={() => {
                engineApiClient.advanceProjectStage(projectId, 'confirmation').catch(() => {})
                navigate(`/project/${projectId}/confirmation`)
              }}
              className="flex items-center gap-1.5 bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-4 py-2 rounded-lg hover:bg-[#ff8f45] transition-colors"
            >
              Continue to Confirmation <ArrowRight size={14} />
            </button>
          </div>
        </div>

        {/* Right — Extracted context + clarification */}
        <div className="hidden lg:block w-64 flex-shrink-0 space-y-4">
          <p className="text-xs uppercase text-gray-400 font-medium">Extracted Understanding</p>

          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-400 shrink-0" />
              <p className="text-white text-sm font-medium">Role Context</p>
            </div>
            <ul className="space-y-1.5">
              {highlights.mustHaves.length === 0 ? (
                <li className="text-gray-500 text-xs italic">Erica will capture must-haves as you talk</li>
              ) : highlights.mustHaves.map((item) => (
                <li key={item} className="flex items-start gap-1.5 text-sm">
                  <span className="text-green-400 mt-0.5 shrink-0">✓</span>
                  <span className="text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {highlights.mustHaves.length < 3 && (
            <div className="bg-[#233D4C] border border-[#FD802E]/30 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <AlertCircle size={14} className="text-[#FD802E] shrink-0" />
                <p className="text-white text-sm font-medium">Still probing</p>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed">
                Erica is working through the brief gaps. Keep the conversation going until the must-haves panel fills in.
              </p>
            </div>
          )}

          {highlights.niceToHaves.length > 0 && (
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
              <p className="text-xs uppercase text-gray-400 font-medium">Nice-to-Haves</p>
              <ul className="space-y-1.5">
                {highlights.niceToHaves.map((item) => (
                  <li key={item} className="flex items-start gap-1.5 text-sm text-gray-300">
                    <span className="text-gray-500 mt-0.5 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Lightbulb size={14} className="text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-gray-400 text-xs leading-relaxed">
                Erica is probing these gaps in real-time. Answers are captured automatically.
              </p>
            </div>
          </div>

          {/* Message count */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-gray-400 text-sm">Messages</span>
            <span className="text-white font-semibold text-sm">{messages.length}</span>
          </div>
        </div>

      </div>
    </div>
  )
}
